import type { Identity } from '../server/identity';
import type { LeaderboardEntry } from '../server/laps';
import type { GhostRecording } from './Ghost';

export const GHOST_LAP_MATCH_TOLERANCE_MS = 2;

export function isOwnLeaderboardRow(row: LeaderboardEntry, id: Identity | null): boolean {
  if (id?.authUserId) return row.user_id === id.authUserId;
  return id !== null && row.user_id === null && row.device_id === id.deviceId;
}

export function ghostMatchesLap(
  ghost: GhostRecording | null,
  totalMs: number | null | undefined,
  toleranceMs = GHOST_LAP_MATCH_TOLERANCE_MS,
): ghost is GhostRecording {
  if (!ghost || typeof totalMs !== 'number' || !Number.isFinite(totalMs)) return false;
  return Math.abs(ghost.lapMs - totalMs) <= toleranceMs;
}

export function getLeaderboardGhostCandidates(
  preferred: LeaderboardEntry | null,
  rows: LeaderboardEntry[],
  id: Identity | null,
  options: { excludeOwn?: boolean; includeSlowerFallback?: boolean } = {},
): LeaderboardEntry[] {
  const out: LeaderboardEntry[] = [];
  const seen = new Set<string>();
  const add = (row: LeaderboardEntry | null): void => {
    if (!row || !row.has_ghost || seen.has(row.id)) return;
    if (options.excludeOwn && isOwnLeaderboardRow(row, id)) return;
    seen.add(row.id);
    out.push(row);
  };

  const preferredIndex = preferred
    ? rows.findIndex((row) => row.id === preferred.id)
    : -1;
  if (preferredIndex >= 0) {
    add(rows[preferredIndex]);
    for (let i = preferredIndex - 1; i >= 0; i--) add(rows[i]);
    if (options.includeSlowerFallback !== false) {
      for (let i = preferredIndex + 1; i < rows.length; i++) add(rows[i]);
    }
    return out;
  }

  add(preferred);
  for (const row of rows) add(row);
  return out;
}
